import { apiErrorSchema, type ApiError } from '@crm/contracts';

/**
 * HTTP client for the API.
 *
 * Every failure is normalised into ApiRequestError so screens never branch on raw fetch behaviour,
 * and the request id is preserved for support.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3380';

/**
 * The access token is kept in memory, never in localStorage: anything readable by a script on the
 * page is readable by an injected one (docs/adr/0008-identity-and-sessions.md).
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * The access token, for the one caller that cannot use a header.
 *
 * A browser cannot set `Authorization` on a WebSocket upgrade, so the realtime gateway takes the
 * token as a query parameter and verifies it exactly as a bearer token (`/v1/realtime`). That is
 * the only reason this is exported: nothing else should be reading it, and it is still never
 * written anywhere a later script could find it.
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/** The API's origin, for building the WebSocket URL. */
export function apiOrigin(): string {
  return API_URL;
}

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
    readonly payload?: ApiError['error'],
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * Renewing an access token before the screen notices (ADR 0008).
 *
 * The token lives fifteen minutes. Nothing renewed it: a tab left open for a quarter of an hour
 * started failing every request with `auth.token_expired`, and because React Query turns a rejected
 * query into an error panel, the product read as *broken* rather than as signed out — "erro ao
 * carregar, tente de novo" on every screen, over and over, until somebody reloaded the page. Three
 * hundred of those in one afternoon of use is what sent me looking.
 *
 * So a 401 is handled where it happens: refresh once, retry once, and only then give up.
 *
 * Two rules keep that from being dangerous. **The refresh is single-flight** — refresh tokens
 * rotate, and presenting a consumed one is treated as theft, so ten queries failing together must
 * ask once and share the answer. And **a retry never retries**: the second attempt is final,
 * whatever it answers, because a loop between 401 and refresh is a loop that never ends.
 */
let refreshing: Promise<string> | null = null;

/** Called when the session is definitively gone, so the app can stop pretending otherwise. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

/** Trades the refresh cookie for a new access token. Shared by every caller that needs one. */
export function refreshAccessToken(): Promise<string> {
  refreshing ??= apiRequest<{ access_token: string }>('/v1/auth/refresh', {
    method: 'POST',
    skipRefresh: true,
  })
    .then((tokens) => {
      accessToken = tokens.access_token;

      return tokens.access_token;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

/** Whether this failure is "your token expired" rather than "you may not do this". */
function expired(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    error.status === 401 &&
    (error.code === 'auth.token_expired' || error.code === 'auth.unauthenticated')
  );
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /**
   * A body to send as-is, for the one endpoint whose payload is a file rather than JSON.
   *
   * Separate from `body` on purpose: `body` is always serialised, and a `File` put through
   * `JSON.stringify` becomes `{}` without anything failing loudly.
   */
  rawBody?: BodyInit;
  signal?: AbortSignal;
  /**
   * What the endpoint answers with. Defaults to JSON.
   *
   * `text` is for the page preview, which returns a rendered HTML document. It shares this
   * function so the session header, the error shape and the request id work the same way; only
   * the last step differs.
   */
  expect?: 'json' | 'text';
  /**
   * Do not try to renew the session for this request.
   *
   * Set on the refresh call itself and on the retry, which are the two requests whose failure must
   * be final. Everything else is renewable.
   */
  skipRefresh?: boolean;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await send<T>(path, options);
  } catch (error) {
    // Not expired, or already the second attempt: the answer stands.
    if (!expired(error) || options.skipRefresh) throw error;

    try {
      await refreshAccessToken();
    } catch {
      // The refresh cookie is gone too. This is a session that has actually ended.
      onSessionLost?.();

      throw error;
    }

    return send<T>(path, { ...options, skipRefresh: true });
  }
}

async function send<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, rawBody, headers, expect: expected = 'json', skipRefresh: _skip, ...rest } = options;
  const payload = rawBody ?? (body === undefined ? undefined : JSON.stringify(body));

  let response: Response;

  try {
    response = await fetch(`${API_URL}${path}`, {
      ...rest,
      credentials: 'include',
      headers: {
        Accept: expected === 'text' ? 'text/html' : 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      ...(payload === undefined ? {} : { body: payload }),
    });
  } catch {
    // Network-level failure: there is no response, so there is no request id to report.
    throw new ApiRequestError(
      0,
      'network.unreachable',
      'Não foi possível contatar o servidor.',
      null,
    );
  }

  const requestId = response.headers.get('x-request-id');
  const text = await response.text();

  /*
   * A failure is JSON whatever the endpoint normally answers with, so the body is parsed as JSON
   * unless the request succeeded and asked for text. A non-JSON error body falls through to the
   * generic message below rather than throwing here.
   */
  const wantsText = expected === 'text' && response.ok;
  let data: unknown = null;

  if (!wantsText && text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(data);

    if (parsed.success) {
      throw new ApiRequestError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        parsed.data.error.request_id ?? requestId,
        parsed.data.error,
      );
    }

    throw new ApiRequestError(
      response.status,
      'internal.error',
      'Ocorreu um erro inesperado.',
      requestId,
    );
  }

  return (wantsText ? text : data) as T;
}

/**
 * Downloads a file the API produces, and hands it to the browser.
 *
 * Not `apiRequest`: that reads the body as text and parses it as JSON, which is right for every
 * other endpoint and wrong for a file. This shares the session header and the error shape, and
 * differs only in what it does with a successful response.
 *
 * The blob URL is revoked immediately after the click. A blob that is never revoked holds its bytes
 * for the life of the document, which on a page somebody exports from three times is three copies
 * of their customer list sitting in memory.
 */
export async function downloadFile(
  path: string,
  options: { method?: string; body?: unknown; fallbackName: string } = { fallbackName: 'export' },
): Promise<{ truncated: boolean }> {
  const response = await fetch(`${API_URL}${path}`, {
    method: options.method ?? 'POST',
    credentials: 'include',
    headers: {
      Accept: 'text/csv, application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  }).catch(() => null);

  if (!response) {
    throw new ApiRequestError(
      0,
      'network.unreachable',
      'Não foi possível contatar o servidor.',
      null,
    );
  }

  if (!response.ok) {
    const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null));

    throw new ApiRequestError(
      response.status,
      parsed.success ? parsed.data.error.code : 'http.error',
      parsed.success ? parsed.data.error.message : 'Não foi possível baixar o arquivo.',
      response.headers.get('x-request-id'),
    );
  }

  // The server names the file; the fallback is for a proxy that strips the header.
  const disposition = response.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition)?.[1];

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = named ?? options.fallbackName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);

  return { truncated: response.headers.get('x-export-truncated') === 'true' };
}
